import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSettings } from '../contexts/SettingsContext';
import { useMosque } from '../contexts/MosqueContext';
import { getIcon } from '../lib/icons';
import TasbihIcon from './TasbihIcon';

const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const { basePath, to } = useMosque();

  const tabs = settings.nav_menu || [];
  const isActive = (path) => {
    const full = to(path);
    if (path === '/') return location.pathname === basePath || location.pathname === `${basePath}/`;
    return location.pathname.startsWith(full);
  };
  const tasbihActive = location.pathname.startsWith(to('/tasbih'));

  const mid = Math.ceil(tabs.length / 2);
  const leftTabs = tabs.slice(0, mid);
  const rightTabs = tabs.slice(mid);

  const renderTab = (tab) => {
    const Icon = getIcon(tab.icon);
    const active = isActive(tab.path);
    return (
      <button
        key={tab.id}
        data-testid={`nav-${tab.id}-tab`}
        onClick={() => navigate(to(tab.path))}
        className="flex flex-1 flex-col items-center gap-0.5 py-1 relative"
      >
        {/* Active indicator — small emerald pill behind icon */}
        <div className="relative flex items-center justify-center w-10 h-7">
          {active && (
            <motion.div
              layoutId="activeTab"
              className="absolute inset-0 rounded-xl"
              style={{ backgroundColor: 'rgba(5, 150, 105, 0.13)' }}
              transition={{ type: 'spring', stiffness: 340, damping: 26 }}
            />
          )}
          <motion.div
            animate={{ scale: active ? 1.06 : 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <Icon
              className="w-[18px] h-[18px] relative z-10"
              style={{ color: active ? '#065F46' : '#C4BFB8' }}
              strokeWidth={active ? 2.4 : 1.8}
            />
          </motion.div>
        </div>
        <span
          className="text-[9.5px] font-medium font-body leading-none"
          style={{ color: active ? '#065F46' : '#B5B0A8' }}
        >
          {tab.label}
        </span>
      </button>
    );
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="max-w-md mx-auto relative">
        {/* Center Tasbih Floating Action Button */}
        <button
          data-testid="nav-tasbih-fab"
          onClick={() => navigate(to('/tasbih'))}
          aria-label="Tasbih Digital"
          className="absolute left-1/2 -translate-x-1/2 -top-7 z-10 flex flex-col items-center"
        >
          <motion.div
            whileTap={{ scale: 0.9 }}
            animate={{ scale: tasbihActive ? 1.06 : 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            className="tasbih-fab-glow w-14 h-14 rounded-full flex items-center justify-center border-4 border-white/90"
            style={{ background: 'linear-gradient(145deg, #10B981 0%, #065F46 60%, #022C22 100%)' }}
          >
            <TasbihIcon variant={settings.tasbih_icon || 'classic'} className="w-7 h-7 text-white" />
          </motion.div>
          <span className="text-[9.5px] font-semibold font-body mt-0.5" style={{ color: tasbihActive ? '#D4AF37' : '#065F46' }}>
            Tasbih
          </span>
        </button>

        {/* Nav bar */}
        <nav
          className="border-t border-stone-100/80"
          style={{
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            boxShadow: '0 -2px 16px rgba(0,0,0,0.06)',
          }}
        >
          <div className="flex items-center px-1 pt-1.5 pb-1.5">
            <div className="flex flex-1 items-center justify-around">{leftTabs.map(renderTab)}</div>
            <div className="w-16 shrink-0" aria-hidden />
            <div className="flex flex-1 items-center justify-around">{rightTabs.map(renderTab)}</div>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default BottomNav;
